import React, { useEffect, useMemo } from 'react'
import { Container, Row, Col, Form, FormGroup, Input } from 'reactstrap'
import { Control, FieldValues, useController, useForm } from 'react-hook-form'
import { LoadIndicator } from 'devextreme-react'
import ICSDateRange from '../../../components/ICSDateRange/ICSDateRange'
import ComponentBox from '../../../components/ComponentBox/ComponentBox'
import { useDispatch } from 'react-redux'
import ICSButton from '../../../components/ICS/Button/ICSButton'
import { getAllResults } from '../../../redux/slices'
import AnalyzerTypeSelector from './AnalyzerTypeSelector'
import AnalyzerPicker from './AnalyzerPicker'
import { useTimezone } from '../../../hooks/useTimezone'
import ICSCheckBoxInput from '../../../components/ICSCheckBoxInput/ICSCheckBoxInput'
import { ResultsSearchValues, ResultsSearchQuery } from '../types'
import './SearchBox.css'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { AppDispatch } from '../../../services/lib'
import useAsyncService from '../../../hooks/useAsyncService'
import { getAllUsers, User } from '../../../services/endpoints/user'
import ICSErrorCard from '../../../components/ICSErrorCard/ICSErrorCard'
import {
  setPageSize,
  setPageIndex,
  setPendingPageIndex,
  RESULTS_CHUNK_SIZE
} from '../../../redux/slices/resultsSlice'

const PAGE_SIZE_OPTIONS = [25, 50, 100]

type SearchBoxProps = {
  isAdmin: boolean
  currentUserId: string | number | null
}

const SearchBox: React.FC<SearchBoxProps> = ({ isAdmin, currentUserId }) => {
  const { formatUtcTimeFromLocal } = useTimezone()
  const dispatch: AppDispatch = useDispatch()
  const currentPageSize =
    useAppSelector((state) => state.results.pageSize) || PAGE_SIZE_OPTIONS[1]

  const {
    serviceValue: users,
    serviceLoading: usersLoading,
    serviceError: usersError,
    setServiceArgs: setUsersServiceArgs
  } = useAsyncService<void, User[]>({
    serviceCall: getAllUsers,
    delay: 0,
    initialArgs: null
  })

  useEffect(() => {
    if (isAdmin) {
      setUsersServiceArgs()
    }
  }, [isAdmin, setUsersServiceArgs])

  const {
    handleSubmit,
    control,
    register,
    getValues,
    formState: { isSubmitting, isValid, isDirty }
  } = useForm<ResultsSearchValues>({
    mode: 'onChange',
    defaultValues: {
      dateRange: {
        start: null,
        end: null
      },
      analyzerType: null,
      analyzerId: null,
      user_id: isAdmin && currentUserId !== null ? String(currentUserId) : '',
      pageSize: currentPageSize,
      hasResults: false,
      recycledOnly: false
    }
  })

  const {
    field: { value: selectedUserValue, onChange: onUserChange }
  } = useController<ResultsSearchValues>({
    name: 'user_id',
    control
  })

  const isFormValid = isValid

  const userOptions = useMemo(() => {
    if (!users || users.length === 0) {
      return [{ label: 'All Users', value: '' }]
    }

    const sortedOptions = [...users]
      .sort((a, b) => a.username.localeCompare(b.username))
      .map((user) => ({
        label: user.username,
        value: String(user.id)
      }))

    return [{ label: 'All Users', value: '' }, ...sortedOptions]
  }, [users])

  const searchData = (data: ResultsSearchValues) => {
    const req: ResultsSearchQuery = {
      analyzer_type: data.analyzerType ?? undefined,
      analyzer_id: data.analyzerId ?? undefined,
      start: formatUtcTimeFromLocal(data.dateRange.start) ?? undefined,
      end: formatUtcTimeFromLocal(data.dateRange.end) ?? undefined,
      offset: 0,
      max: RESULTS_CHUNK_SIZE
    }

    if (data.hasResults) {
      req.hasResults = true
    }
    if (data.recycledOnly) {
      req.recycled = true
    }

    if (isAdmin) {
      const selectedUserId = String(data.user_id ?? '').trim()

      if (selectedUserId !== '') {
        req.user_id = selectedUserId
      }
    } else if (currentUserId !== null) {
      req.user_id = currentUserId
    }

    dispatch(setPageSize(data.pageSize))
    dispatch(setPageIndex(0))
    dispatch(setPendingPageIndex(null))
    dispatch(
      getAllResults({
        query: _toApiQuery(req),
        meta: { desiredPageIndex: 0, replace: true }
      })
    )
  }

  return (
    <Container fluid={true}>
      <Form onSubmit={handleSubmit(searchData)}>
        <ComponentBox
          isCustom={true}
          cardClass="groups-results-container"
          cardBodyId="groupsCardBody"
          title="Search Criteria"
          accordion={true}
        >
          <Row>
            <Col xs={12} md={6}>
              <div>
                <div className="spacer">
                  <h4>Date Range</h4>
                  <ICSDateRange name="dateRange" control={control} />
                </div>
              </div>

              <div className="spacer">
                <h4>Additional Options</h4>
                <ICSCheckBoxInput
                  name="hasResults"
                  control={control as unknown as Control<FieldValues>}
                  errors={{}}
                  label="Only show results with files"
                  defaultValue={getValues('hasResults')}
                />
                {isAdmin && (
                  <ICSCheckBoxInput
                    name="recycledOnly"
                    control={control as unknown as Control<FieldValues>}
                    errors={{}}
                    label="Only show recycled results"
                    defaultValue={getValues('recycledOnly')}
                  />
                )}
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div>
                <div className="spacer">
                  <h4>Analyzer Type</h4>
                  <AnalyzerTypeSelector control={control} name="analyzerType" />
                </div>
                <div className="spacer">
                  <h4>Analyzer</h4>
                  <AnalyzerPicker control={control} name="analyzerId" />
                </div>
                {isAdmin && (
                  <div className="spacer">
                    <h4>User</h4>
                    {usersLoading ? (
                      <LoadIndicator />
                    ) : usersError ? (
                      <ICSErrorCard
                        code="error"
                        message={usersError}
                        fields={[]}
                      />
                    ) : (
                      <FormGroup>
                        <Input
                          id="resultsUserPicker"
                          type="select"
                          value={
                            typeof selectedUserValue === 'string'
                              ? selectedUserValue
                              : ''
                          }
                          onChange={(event) => onUserChange(event.target.value)}
                        >
                          {userOptions.map((option) => (
                            <option
                              key={`resultsUserOption${option.value}`}
                              value={option.value}
                            >
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    )}
                  </div>
                )}
              </div>
            </Col>
          </Row>
          <Row className="dataSearchButtonsRow">
            <Col className="dataSearchButtonCol" />
            <Col className="col-sm-auto dataSearchButtonCol">
              <ICSButton
                className="dataSearchButton"
                icon="search"
                text="Search"
                type="success"
                onClick={handleSubmit(searchData)}
                disabled={isSubmitting || !isFormValid || !isDirty}
              />
            </Col>
          </Row>
        </ComponentBox>
      </Form>
    </Container>
  )
}

export default SearchBox

const _toApiQuery = (
  query: ResultsSearchQuery
): Record<string, string | number> => {
  return Object.entries(query).reduce<Record<string, string | number>>(
    (acc, [key, value]) => {
      if (value === undefined || value === null) {
        return acc
      }

      if (typeof value === 'string' && value.trim() === '') {
        return acc
      }

      if (typeof value === 'boolean') {
        acc[key] = value ? 'true' : 'false'
      } else {
        acc[key] = value as string | number
      }

      return acc
    },
    {}
  )
}
